import { ClipboardPenLineIcon, EarthIcon, type LucideIcon, PlugIcon } from 'lucide-react'

import {
  type InherentRiskGroup,
  InherentRiskItem,
  InherentRiskSource,
} from '@/gen/inventory/v1/company_service_pb'

import { inherentRiskSourceLabel } from '@/const/label'
import { inherentRiskSourcePriority } from '@/const/priority'

import { cn } from '@/lib/ui/style-helpers'

import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import type {
  AnswerGroup,
  SourceDisplay,
  SourceGroupDisplay,
} from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/types'

import { IntegrationLogo } from '@/components/shared/integration-logo/integration-logo'

const iconNode = (Icon: LucideIcon, className: string) => (
  <Icon className={cn('size-4', className)} />
)

export const toSourceDisplay = (item: InherentRiskItem): SourceDisplay | undefined => {
  const isIntake = item.source === InherentRiskSource.INTAKE
  const permissions = item.permissions

  // Integration items (ZIP, OKTA, WEBHOOK, etc.) carry their display data on
  // integrationSource regardless of which specific enum value they use.
  if (item.integrationSource?.name) {
    return {
      key: `${item.source}-${item.integrationSource.name}-${item.id}`,
      name: item.integrationSource.name,
      icon: item.integrationSource.imgUrl ? (
        <IntegrationLogo
          src={item.integrationSource.imgUrl}
          name={item.integrationSource.name}
          decorative
        />
      ) : (
        iconNode(ClipboardPenLineIcon, 'text-main-foreground-tertiary')
      ),
      evidence: item.explanation,
      evidenceQuotes: item.evidenceQuotes,
      permissions,
      isIntake,
    }
  }

  switch (item.source) {
    case InherentRiskSource.PROJECTED:
      return {
        key: `projected-${item.id}`,
        name: SET_IRQ_MODAL_LABEL.LEMA_SMART_ESTIMATION,
        icon: iconNode(EarthIcon, 'text-main-foreground-ai'),
        evidence: item.explanation,
        permissions,
        isIntake,
      }
    case InherentRiskSource.INTAKE:
      return {
        key: `intake-${item.id}`,
        name: SET_IRQ_MODAL_LABEL.LEMA_INTAKE,
        icon: iconNode(ClipboardPenLineIcon, 'text-main-foreground-tertiary'),
        evidence: item.explanation,
        permissions,
        isIntake,
      }
    default:
      return undefined
  }
}

export const getSourceGroupKey = (item: InherentRiskItem): string | undefined => {
  if (item.integrationSource?.name) return `integration:${item.integrationSource.name}`
  switch (item.source) {
    case InherentRiskSource.UNSPECIFIED:
    case InherentRiskSource.IRQ:
      return undefined
    default:
      return `source:${item.source}`
  }
}

export const toSourceGroupDisplay = (item: InherentRiskItem): SourceGroupDisplay | undefined => {
  const key = getSourceGroupKey(item)
  if (!key) return undefined
  if (item.integrationSource?.name) {
    return {
      key,
      name: item.integrationSource.name,
      icon: item.integrationSource.imgUrl ? (
        <IntegrationLogo
          src={item.integrationSource.imgUrl}
          name={item.integrationSource.name}
          decorative
        />
      ) : (
        iconNode(PlugIcon, 'text-main-foreground-tertiary')
      ),
    }
  }
  switch (item.source) {
    case InherentRiskSource.PROJECTED:
      return {
        key,
        name: SET_IRQ_MODAL_LABEL.LEMA_SMART_ESTIMATION,
        icon: iconNode(EarthIcon, 'text-main-foreground-ai'),
      }
    case InherentRiskSource.INTAKE:
      return {
        key,
        name: SET_IRQ_MODAL_LABEL.LEMA_INTAKE,
        icon: iconNode(ClipboardPenLineIcon, 'text-main-foreground-tertiary'),
      }
    default:
      return {
        key,
        name: inherentRiskSourceLabel[item.source] || 'Source',
        icon: iconNode(PlugIcon, 'text-main-foreground-tertiary'),
      }
  }
}

// Sources offered as filter toggles in the IRQ Overview — derived from the items
// actually present in lemaItems, deduped by source key, sorted by priority.
export const getAvailableSources = (lemaItems: InherentRiskGroup[]): SourceGroupDisplay[] => {
  const byKey = new Map<string, { display: SourceGroupDisplay; priority: number; order: number }>()
  let order = 0
  for (const group of lemaItems) {
    for (const item of group.inherentRiskItems) {
      const display = toSourceGroupDisplay(item)
      if (!display || byKey.has(display.key)) continue
      byKey.set(display.key, {
        display,
        priority: inherentRiskSourcePriority[item.source],
        order: order++,
      })
    }
  }
  return [...byKey.values()]
    .sort((a, b) => a.priority - b.priority || a.order - b.order)
    .map(({ display }) => display)
}

// Default source selection for the IRQ Overview checkboxes. When the risk is
// already validated we don't pre-apply any Lema source. When it's estimated, we
// pre-apply intake if present, otherwise everything.
export const getDefaultSelectedSourceKeys = (
  lemaItems: InherentRiskGroup[],
  isEstimated: boolean,
): Set<string> => {
  if (!isEstimated) return new Set<string>()

  const intakeItem = lemaItems
    .flatMap((g) => g.inherentRiskItems)
    .find((i) => i.source === InherentRiskSource.INTAKE)
  const intakeKey = intakeItem && getSourceGroupKey(intakeItem)
  if (intakeKey) return new Set([intakeKey])

  return new Set(getAvailableSources(lemaItems).map((s) => s.key))
}

export const groupByAnswer = (suggestions: InherentRiskItem[]): AnswerGroup[] => {
  const byId = new Map<string, AnswerGroup>()
  for (const item of suggestions) {
    if (!item.id) continue
    const display = toSourceDisplay(item)
    if (!display) continue
    const group = byId.get(item.id) ?? {
      id: item.id,
      displayName: item.displayName,
      sources: [],
    }
    group.sources.push(display)
    byId.set(item.id, group)
  }
  return [...byId.values()]
}
